import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, Clock, Users, DollarSign, Target } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

import api from '../utils/api';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

function BarChart({ data, valueKey, labelKey, title, color = '#6366f1', prefix = '' }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="space-y-2">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-16 shrink-0 text-right truncate">{item[labelKey]}</span>
            <div className="flex-1 h-7 bg-gray-100 rounded-lg overflow-hidden relative">
              <div
                className="h-full rounded-lg transition-all duration-500"
                style={{ width: `${Math.max((item[valueKey] / maxVal) * 100, 2)}%`, backgroundColor: color }}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600">
                {prefix}{typeof item[valueKey] === 'number' ? item[valueKey].toLocaleString() : item[valueKey]}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.count, 0);
  let cumulative = 0;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="flex items-center gap-6">
        <svg viewBox="0 0 36 36" className="w-28 h-28 shrink-0">
          {data.map((item, i) => {
            const pct = (item.count / total) * 100;
            const offset = 100 - cumulative;
            cumulative += pct;
            return (
              <circle
                key={i}
                cx="18" cy="18" r="15.9155"
                fill="none"
                stroke={COLORS[i % COLORS.length]}
                strokeWidth="3.5"
                strokeDasharray={`${pct} ${100 - pct}`}
                strokeDashoffset={offset}
                strokeLinecap="round"
              />
            );
          })}
          <text x="18" y="18" textAnchor="middle" dy=".3em" className="fill-gray-900 text-[5px] font-bold">{total}</text>
        </svg>
        <div className="space-y-1.5 flex-1">
          {data.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-gray-600 flex-1 capitalize">{item.condition || item.source}</span>
              <span className="font-medium text-gray-900">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdvancedAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('90');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: analytics } = await api.get(`/api/analytics/advanced?days=${period}`);
      setData(analytics);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const totalRevenue = data.revenue_trend.reduce((sum, r) => sum + r.revenue, 0);
  const totalProfit = data.revenue_trend.reduce((sum, r) => sum + r.profit, 0);
  const totalSold = data.revenue_trend.reduce((sum, r) => sum + r.count, 0);

  return (
    <div data-testid="advanced-analytics">
      {/* Period Selector */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-xl">Advanced Analytics</h3>
            <p className="text-gray-500 text-sm">Performance insights and trends</p>
          </div>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-32 rounded-xl" data-testid="analytics-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="analytics-revenue">${totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Net Profit</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600" data-testid="analytics-profit">${totalProfit.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Avg Days on Lot</span>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="analytics-days">{data.avg_days_on_lot}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-indigo-500" />
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">CRM Close Rate</span>
          </div>
          <p className="text-2xl font-bold text-indigo-600" data-testid="analytics-close-rate">{data.crm_summary.close_rate}%</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <BarChart
            data={data.revenue_trend}
            valueKey="revenue"
            labelKey="month"
            title="Monthly Revenue"
            color="#22c55e"
            prefix="$"
          />
          {data.revenue_trend.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No sales data yet. Sell vehicles to see revenue trends.</div>
          )}
        </div>

        {/* Profit Trend */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <BarChart
            data={data.revenue_trend}
            valueKey="profit"
            labelKey="month"
            title="Monthly Profit"
            color="#6366f1"
            prefix="$"
          />
          {data.revenue_trend.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No profit data yet.</div>
          )}
        </div>

        {/* Top Makes */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <BarChart
            data={data.top_makes}
            valueKey="count"
            labelKey="make"
            title="Top Selling Makes"
            color="#f59e0b"
          />
          {data.top_makes.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No sold vehicles yet.</div>
          )}
        </div>

        {/* Condition Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <DonutChart data={data.condition_breakdown} title="Inventory by Condition" />
          {data.condition_breakdown.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No inventory data.</div>
          )}
        </div>

        {/* Lead Sources */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <BarChart
            data={data.lead_sources}
            valueKey="total"
            labelKey="source"
            title="Lead Sources (CRM)"
            color="#8b5cf6"
          />
          {data.lead_sources.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No CRM data yet.</div>
          )}
        </div>

        {/* CRM Summary */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">CRM Performance</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <Users className="w-5 h-5 text-indigo-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-900">{data.crm_summary.total_contacts}</p>
              <p className="text-xs text-gray-500">Total Contacts</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <Target className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">{data.crm_summary.won}</p>
              <p className="text-xs text-gray-500">Deals Won</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-500">{data.crm_summary.lost}</p>
              <p className="text-xs text-gray-500">Deals Lost</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{data.crm_summary.close_rate}%</p>
              <p className="text-xs text-gray-500">Close Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-5 border border-indigo-100">
        <p className="text-sm text-indigo-700">
          <strong>Period Summary:</strong> {totalSold} vehicles sold generating ${totalRevenue.toLocaleString()} in revenue and ${totalProfit.toLocaleString()} in profit over the last {period} days.
          {data.avg_days_on_lot > 0 && ` Average time on lot: ${data.avg_days_on_lot} days.`}
        </p>
      </div>
    </div>
  );
}
